// Fiche d'une mission : consignes, intervention, carte, début de mission, rapports enregistrés
// (état local : enregistré sur cet appareil, synchronisé, conflit) et nouveau rapport.
import type { EtatRapportMission as EtatRapportServeur } from '@cc/contrats';
import { Bouton, EtatVide, LienBouton, Squelette } from '@cc/ui';
import { Play } from 'lucide-react';
import { useState } from 'react';
import { useParams } from 'react-router';
import { messageErreur } from '../../api/erreurs';
import { positionContrat } from '../../terrain/appareil';
import { LIBELLES_ETAT_MISSION, LIBELLES_PRIORITE, LIBELLES_TYPE_RAPPORT, dateHeure, dateLongue, etatLocalObjet, etatLocalOperation } from '../../terrain/etat-local';
import { uuidV7 } from '../../terrain/identifiants';
import { releverPosition } from '../../terrain/position';
import { synchroniseurPartage } from '../../terrain/synchronisation';
import { useMissionLocale, useOperationsLocales, useTravailLocal } from '../../terrain/useTerrain';
import { CarteTerrain } from './CarteTerrain';
import { Bloc, Definitions, EnTeteTerrain, EtatLocal, Ligne, Liste, Mono, Section, TexteEtat } from './commun';
import { FormulaireRapport } from './FormulaireRapport';
import styles from './terrain.module.css';

const LIBELLES_RAPPORT_SERVEUR: Record<EtatRapportServeur, string> = { synchronise: 'Reçu par le serveur', conflit: 'Conflit', retenu: 'Retenu par le responsable', ecarte: 'Écarté par le responsable' };

export function PageMission() {
  const { id } = useParams();
  const mission = useMissionLocale(id);
  const operations = useOperationsLocales(id);
  const travail = useTravailLocal();
  const [debutEnCours, setDebutEnCours] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (mission.chargement) return <Squelette lignes={6} libelle="Lecture de la mission sur l'appareil" />;
  const m = mission.donnees;
  if (!m) {
    return (
      <>
        <EnTeteTerrain titre="Mission introuvable" retour="/terrain" />
        <EtatVide titre="Cette mission n'est pas enregistrée sur cet appareil" niveau={2} action={<LienBouton href="/terrain">Retour à mon travail</LienBouton>}>
          <p>Elle a pu être réaffectée ou retirée lors de la dernière synchronisation.</p>
        </EtatVide>
      </>
    );
  }
  const ops = operations.donnees ?? [];
  const debutEnregistre = ops.some((o) => o.type === 'debut_mission');
  const rapportsLocaux = ops.filter((o) => o.type === 'rapport_mission');
  const idsLocaux = new Set(rapportsLocaux.map((o) => o.id_client));
  const rapportsServeur = m.rapports.filter((r) => !r.id_client || !idsLocaux.has(r.id_client));
  const categorie = travail.donnees?.referentiels.categories.find((c) => c.code === m.intervention.categorie)?.libelle ?? m.intervention.categorie;
  const etatLocal = etatLocalObjet(ops, m.id);

  const commencer = async () => {
    setDebutEnCours(true);
    setMessage(null);
    try {
      const { position, erreur } = await releverPosition(8_000);
      await synchroniseurPartage().enfiler(
        { id_client: uuidV7(), type: 'debut_mission', objet_type: 'mission', objet_id: m.id, version_attendue: m.version, enregistre_le: new Date().toISOString(), charge: position ? { position: positionContrat(position) } : {} },
        { titre: 'Début de mission', objet_libelle: `${m.intervention.numero} · ${m.intervention.titre}` },
      );
      setMessage(`Début de mission enregistré sur cet appareil${position ? ' avec votre position' : erreur ? ` (${erreur})` : ''}.`);
    } catch (e) {
      setMessage(messageErreur(e));
    } finally {
      setDebutEnCours(false);
    }
  };

  return (
    <>
      <EnTeteTerrain
        titre={m.intervention.titre}
        surTitre={m.intervention.numero}
        retour="/terrain"
        libelleRetour="Mon travail"
        sousTitre={
          <>
            {LIBELLES_PRIORITE[m.intervention.priorite]} · {categorie} · {LIBELLES_ETAT_MISSION[m.etat]}
            {etatLocal ? (
              <>
                {' · '}
                <EtatLocal etat={etatLocal} />
              </>
            ) : null}
          </>
        }
        actions={
          m.etat === 'affectee' && !debutEnregistre ? (
            <Bouton icone={Play} onClick={() => void commencer()} chargement={debutEnCours} libelleChargement="Relevé de la position en cours">
              Commencer la mission
            </Bouton>
          ) : debutEnregistre ? (
            <TexteEtat libelle="Mission commencée sur cet appareil" ton="info" />
          ) : null
        }
      />
      {message ? (
        <p role="status" className={styles.aide}>
          {message}
        </p>
      ) : null}
      <Bloc etiquette="Consignes">
        <p className={styles.texte}>{m.consignes ?? 'Aucune consigne particulière.'}</p>
      </Bloc>
      <Definitions
        elements={[
          { terme: 'Adresse', valeur: m.intervention.adresse ?? 'Non renseignée' },
          { terme: 'Équipe', valeur: m.equipe?.nom ?? 'Non renseignée' },
          { terme: 'Date', valeur: dateLongue(m.date) },
          { terme: 'Version de la mission', valeur: <Mono>{m.version}</Mono> },
          ...(m.intervention.description ? [{ terme: 'Description du signalement', valeur: m.intervention.description }] : []),
        ]}
      />
      {m.intervention.position ? <CarteTerrain titre="Carte de la mission" cible={{ libelle: m.intervention.titre, position: m.intervention.position }} suivre /> : <p className={styles.aide}>Aucune position connue pour cette intervention.</p>}
      <Section titre="Rapports" droite={<span className={styles.aide}>{rapportsLocaux.length + rapportsServeur.length}</span>}>
        {rapportsLocaux.length + rapportsServeur.length === 0 ? (
          <p className={styles.aide}>Aucun rapport pour cette mission.</p>
        ) : (
          <Liste libelle="Rapports de la mission">
            {rapportsLocaux.map((o) => {
              const e = etatLocalOperation(o);
              return <Ligne key={o.id_client} href={e === 'conflit' ? '/terrain/conflits' : undefined} titre={o.titre} sousTitre={`Enregistré le ${dateHeure(o.enregistre_le)}${o.synchronisee_le ? ` · synchronisé le ${dateHeure(o.synchronisee_le)}` : ''}${o.etat === 'rejetee' ? ` · rejeté : ${o.resultat?.motif ?? 'sans motif'}` : ''}`} droite={e ? <EtatLocal etat={e} /> : <TexteEtat libelle="Rejeté" ton="attention" />} />;
            })}
            {rapportsServeur.map((r) => (
              <Ligne key={r.id} titre={LIBELLES_TYPE_RAPPORT[r.type]} sousTitre={`Reçu par le serveur le ${dateHeure(r.recu_le)}`} droite={<TexteEtat libelle={LIBELLES_RAPPORT_SERVEUR[r.etat]} ton={r.etat === 'conflit' || r.etat === 'ecarte' ? 'attention' : 'ok'} />} />
            ))}
          </Liste>
        )}
      </Section>
      <Section titre="Nouveau rapport">
        <FormulaireRapport mission={m} typeInitial={rapportsLocaux.length + rapportsServeur.length === 0 ? 'avant' : 'apres'} />
      </Section>
    </>
  );
}
